import { useState } from "react";
import axios from "axios";
import { Check } from "lucide-react";
import Navigation from "@/components/Navigation";
import ChatbotWidget from "@/components/ChatbotWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "/assets/logo.png";

export default function DeleteAccountPage() {
  const [form, setForm] = useState({ email: "", reason: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      setStatus("error");
      setErrorMsg("Please enter the email address associated with your account.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/delete-request`, form);
      setStatus("success");
      setForm({ email: "", reason: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <div className="star-bg-fixed" aria-hidden="true" />
      <div className="star-tint min-h-screen relative">
        <Navigation />

        <section
          id="delete-account"
          className="pt-40 pb-16 sm:pt-48 sm:pb-20 border-b border-white/5"
          data-testid="delete-account-section"
        >
          <div className="max-w-3xl mx-auto px-6 sm:px-12 text-center">
            <img src={LOGO} alt="AirStageX" className="h-16 sm:h-20 mx-auto mb-10 opacity-95" />
            <p className="overline text-[#007AFF] mb-6 drift">Account &amp; Data</p>
            <h1 className="font-heading font-light text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] mb-7">
              Delete Your Account
            </h1>
            <p className="text-zinc-200 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
              You can request the permanent deletion of your AirStageX account and associated personal data at
              any time. Submit the form below and our team will process your request within 30 days, in line with
              our Privacy Policy.
            </p>
          </div>
        </section>

        <section className="py-16 sm:py-24" data-testid="delete-account-form-section">
          <div className="max-w-2xl mx-auto px-6 sm:px-12">
            <div className="text-zinc-300 text-sm leading-relaxed mb-12 space-y-3 text-left">
              <p className="text-white font-medium">What gets deleted</p>
              <p>
                Your personal details (name, email, location and preferences), any waitlist or partnership
                enquiries you submitted, and any messages you sent us will be permanently removed from our
                records. This action cannot be undone.
              </p>
            </div>

            {status === "success" ? (
              <div
                className="flex flex-col items-center justify-center text-center py-16"
                data-testid="delete-account-success"
              >
                <div className="w-14 h-14 border border-[#007AFF] flex items-center justify-center mb-6 rounded-full">
                  <Check className="w-6 h-6 text-[#007AFF]" />
                </div>
                <h3 className="font-heading text-2xl font-light text-white mb-3">Request received</h3>
                <p className="text-zinc-300 text-base max-w-md">
                  Your account deletion request has been received. We will process it within 30 days and confirm
                  by email once complete.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="delete-account-form">
                <div>
                  <label className="overline text-zinc-300 mb-2 block">Account email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-transparent border-b border-white/30 px-0 py-3 text-white focus:outline-none focus:border-[#007AFF] transition-colors placeholder:text-zinc-500 w-full text-sm"
                    placeholder="The email associated with your account"
                    required
                    data-testid="delete-account-email"
                  />
                </div>
                <div>
                  <label className="overline text-zinc-300 mb-2 block">Reason (optional)</label>
                  <textarea
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    rows={4}
                    className="bg-transparent border-b border-white/30 px-0 py-3 text-white focus:outline-none focus:border-[#007AFF] transition-colors placeholder:text-zinc-500 w-full text-sm resize-y"
                    placeholder="Let us know why you're leaving (optional)"
                    data-testid="delete-account-reason"
                  />
                </div>
                {status === "error" && (
                  <p className="text-red-400 text-sm" data-testid="delete-account-error">{errorMsg}</p>
                )}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="btn-pill btn-pill-primary w-full disabled:opacity-50"
                  data-testid="delete-account-submit"
                >
                  {status === "loading" ? "Submitting..." : "Request Account Deletion"}
                </button>
                <p className="text-zinc-500 text-xs text-center">
                  Prefer to email us? Write to{" "}
                  <a href="mailto:enquiries@airstagex.com" className="text-[#007AFF] hover:underline" data-testid="delete-account-email-link">
                    enquiries@airstagex.com
                  </a>.
                </p>
              </form>
            )}
          </div>
        </section>

        <footer className="py-12 border-t border-white/5" data-testid="delete-account-footer">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-zinc-400 text-xs">All rights reserved &copy; 2025 AirStageX</p>
            <a href="/" className="text-zinc-300 text-xs hover:text-white transition-colors" data-testid="delete-account-back-home">
              Back to home
            </a>
          </div>
        </footer>

        <ChatbotWidget />
      </div>
    </>
  );
}
